import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useGetShopProductsQuery,
  useCreateShopProductMutation,
  useUpdateShopProductMutation,
  useDeleteShopProductMutation,
} from '../store/services/shopProducts';
import { useGetProductsQuery } from '../store/services/products';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const validationSchema = yup.object({
  product_id: yup.string().required('Product is required'),
  price: yup.number().required('Price is required').positive('Price must be positive'),
  product_subcategory: yup.string(),
});

interface ShopProductFormValues {
  product_id: string;
  price: number;
  product_subcategory?: string;
}

export default function ShopProducts() {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedShopProduct, setSelectedShopProduct] = useState<any | null>(null);
  
  // Get the current user from Redux state
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  // Check if user has permission to manage shop products
  const canManageProducts = currentUser?.role === 'SuperAdmin' || currentUser?.role === 'ShopOwner';
  
  // Get the shop_id from the current user (for ShopOwner)
  const shop_id = currentUser?.shop_id;
  
  // Fetch shop products for the current shop
  const { data: shopProducts, isLoading, refetch } = useGetShopProductsQuery(shop_id);
  
  // Fetch all products for dropdown selection
  const { data: products } = useGetProductsQuery();
  
  const [createShopProduct] = useCreateShopProductMutation();
  const [updateShopProduct] = useUpdateShopProductMutation();
  const [deleteShopProduct] = useDeleteShopProductMutation();

  // When user changes, refetch shop products
  useEffect(() => {
    if (shop_id) {
      refetch();
    }
  }, [shop_id, refetch]);

  const formik = useFormik<ShopProductFormValues>({
    initialValues: {
      product_id: '',
      price: 0,
      product_subcategory: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedShopProduct) {
          // Update existing shop product
          await updateShopProduct({
            id: selectedShopProduct._id,
            data: {
              price: values.price,
              product_subcategory: values.product_subcategory || undefined,
            },
          }).unwrap();
          setSnackbar({ open: true, message: 'Shop product updated successfully', severity: 'success' });
        } else {
          // Create new shop product
          if (!shop_id) {
            setSnackbar({ open: true, message: 'No shop associated with your account', severity: 'error' });
            return;
          }
          
          await createShopProduct({
            shop_id,
            product_id: values.product_id,
            price: values.price,
            product_subcategory: values.product_subcategory || undefined,
          }).unwrap();
          setSnackbar({ open: true, message: 'Product added to shop successfully', severity: 'success' });
        }
        handleClose();
      } catch (error) {
        console.error('Failed to save shop product:', error);
        setSnackbar({ open: true, message: 'Failed to save shop product', severity: 'error' });
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedShopProduct(null);
    formik.resetForm();
    setOpen(true);
  };

  const handleEdit = (shopProduct: any) => {
    setSelectedShopProduct(shopProduct);
    
    // Find the corresponding product
    formik.setValues({
      product_id: shopProduct.product_id,
      price: shopProduct.price,
      product_subcategory: shopProduct.product_subcategory || '',
    });
    
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this product from your shop?')) {
      try {
        await deleteShopProduct(id).unwrap();
        setSnackbar({ open: true, message: 'Product removed from shop successfully', severity: 'success' });
      } catch (error) {
        console.error('Failed to delete shop product:', error);
        setSnackbar({ open: true, message: 'Failed to remove product from shop', severity: 'error' });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Find product details by ID
  const findProductName = (productId: string) => {
    const product = products?.find(p => p._id === productId);
    return product ? product.name : 'Unknown Product';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shop_id && currentUser?.role === 'ShopOwner') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No shop is associated with your account. Please contact an administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Shop Products</Typography>
        {canManageProducts && (
          <Button variant="contained" onClick={handleClickOpen}>
            Add Product to Shop
          </Button>
        )}
      </Box>

      {!canManageProducts && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to shop products.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Product Subcategory</TableCell>
              {canManageProducts && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {shopProducts?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManageProducts ? 4 : 3} align="center">
                  No products in this shop yet
                </TableCell>
              </TableRow>
            ) : (
              shopProducts?.map((shopProduct) => (
                <TableRow key={shopProduct._id}>
                  <TableCell>{findProductName(shopProduct.product_id)}</TableCell>
                  <TableCell>${shopProduct.price.toFixed(2)}</TableCell>
                  <TableCell>{shopProduct.product_subcategory || '-'}</TableCell>
                  {canManageProducts && (
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(shopProduct)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(shopProduct._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedShopProduct ? 'Edit Shop Product' : 'Add Product to Shop'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              select
              fullWidth
              margin="dense"
              id="product_id"
              name="product_id"
              label="Product"
              value={formik.values.product_id}
              onChange={formik.handleChange}
              error={formik.touched.product_id && Boolean(formik.errors.product_id)}
              helperText={formik.touched.product_id && formik.errors.product_id}
              disabled={!!selectedShopProduct} // Disable product selection when editing
            >
              {products?.map((product) => (
                <MenuItem key={product._id} value={product._id}>
                  {product.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="dense"
              id="price"
              name="price"
              label="Price"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              value={formik.values.price}
              onChange={formik.handleChange}
              error={formik.touched.price && Boolean(formik.errors.price)}
              helperText={formik.touched.price && formik.errors.price}
            />

            <TextField
              fullWidth
              margin="dense"
              id="product_subcategory"
              name="product_subcategory"
              label="Product Subcategory"
              value={formik.values.product_subcategory}
              onChange={formik.handleChange}
              error={formik.touched.product_subcategory && Boolean(formik.errors.product_subcategory)}
              helperText={formik.touched.product_subcategory && formik.errors.product_subcategory}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedShopProduct ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}